import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Settings as SettingsIcon, Save, AlertCircle } from "lucide-react";

export default function CommissionSettingsCard({ onSaved }) {
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState({
        l1_bonus: 500, l2_bonus: 200, l3_bonus: 100,
        platform_pct: 15, creator_pct: 5, league_pct: 2,
        point_to_peso_rate: 1,
    });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/settings");
            setSettings(data);
            setForm({
                l1_bonus: data.l1_bonus,
                l2_bonus: data.l2_bonus,
                l3_bonus: data.l3_bonus,
                platform_pct: Math.round(data.platform_pct * 10000) / 100,
                creator_pct: Math.round(data.creator_pct * 10000) / 100,
                league_pct: Math.round(data.league_pct * 10000) / 100,
                point_to_peso_rate: data.point_to_peso_rate || 1,
            });
        } catch (e) { /* noop */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    const sumFees = Number(form.platform_pct || 0) + Number(form.creator_pct || 0) + Number(form.league_pct || 0);
    const playersPct = Math.max(0, 100 - sumFees);
    const invalid = sumFees >= 100;

    const save = async () => {
        if (invalid) {
            toast.error("Las comisiones suman 100%+. Reduce alguna.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                l1_bonus: Number(form.l1_bonus),
                l2_bonus: Number(form.l2_bonus),
                l3_bonus: Number(form.l3_bonus),
                platform_pct: Number(form.platform_pct) / 100,
                creator_pct: Number(form.creator_pct) / 100,
                league_pct: Number(form.league_pct) / 100,
                point_to_peso_rate: Number(form.point_to_peso_rate),
            };
            const { data } = await api.patch("/admin/settings", payload);
            setSettings(data);
            toast.success("Configuración guardada");
            onSaved?.(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    if (!settings) {
        return <div className="h-44 rounded-2xl bg-p2w-surface border border-p2w-line animate-pulse" />;
    }

    const exampleSplit = (pct) => Math.round(10000 * (pct / 100));

    return (
        <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-6 mb-6" data-testid="commission-settings-card">
            <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-p2w-green/15 border border-p2w-green/40 flex items-center justify-center">
                    <SettingsIcon className="w-5 h-5 text-p2w-green" />
                </div>
                <div>
                    <h3 className="font-sora font-bold text-lg">Configuración manual de comisiones</h3>
                    <p className="text-xs text-p2w-gray mt-1">
                        Modelo parimutuel. Las 3 comisiones salen del pozo y suman menos de 100%. El resto va a jugadores ganadores proporcional al stake.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <NumberField
                    label="Bonus L1 (pts/$)"
                    value={form.l1_bonus}
                    onChange={(v) => setForm({ ...form, l1_bonus: v })}
                    testid="settings-l1-input"
                    accent="text-p2w-green border-p2w-green/40"
                />
                <NumberField
                    label="Bonus L2 (pts/$)"
                    value={form.l2_bonus}
                    onChange={(v) => setForm({ ...form, l2_bonus: v })}
                    testid="settings-l2-input"
                    accent="text-p2w-purple border-p2w-purple/40"
                />
                <NumberField
                    label="Bonus L3 (pts/$)"
                    value={form.l3_bonus}
                    onChange={(v) => setForm({ ...form, l3_bonus: v })}
                    testid="settings-l3-input"
                    accent="text-p2w-magenta border-p2w-magenta/40"
                />
            </div>

            <div className="rounded-xl border border-p2w-line bg-p2w-elevated p-4 mb-4">
                <div className="text-xs uppercase tracking-widest text-p2w-gray mb-3">Reparto del pozo por mercado</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <NumberField
                        label="Plataforma %"
                        value={form.platform_pct}
                        onChange={(v) => setForm({ ...form, platform_pct: v })}
                        step={0.5}
                        testid="settings-platform-input"
                        accent="text-p2w-purple border-p2w-purple/40"
                        suffix="%"
                    />
                    <NumberField
                        label="Creador %"
                        value={form.creator_pct}
                        onChange={(v) => setForm({ ...form, creator_pct: v })}
                        step={0.5}
                        testid="settings-creator-input"
                        accent="text-p2w-magenta border-p2w-magenta/40"
                        suffix="%"
                    />
                    <NumberField
                        label="Liga mensual %"
                        value={form.league_pct}
                        onChange={(v) => setForm({ ...form, league_pct: v })}
                        step={0.5}
                        testid="settings-league-input"
                        accent="text-p2w-amber border-p2w-amber/40"
                        suffix="%"
                    />
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-p2w-gray">Jugadores (auto)</Label>
                        <div
                            className={`mt-1.5 rounded-md border bg-p2w-bg px-3 py-2 font-sora font-bold text-xl number-tabular ${
                                invalid ? "border-p2w-red/60 text-p2w-red" : "border-p2w-green/40 text-p2w-green"
                            }`}
                            data-testid="settings-players-pct"
                        >
                            {playersPct.toFixed(1)}%
                        </div>
                    </div>
                </div>
                {invalid && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-p2w-red">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Las comisiones suman {sumFees.toFixed(1)}%. Deben dejar margen positivo para los jugadores.
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                <NumberField
                    label="Conversión 1 pt = X pesos"
                    value={form.point_to_peso_rate}
                    onChange={(v) => setForm({ ...form, point_to_peso_rate: v })}
                    step={0.1}
                    testid="settings-rate-input"
                    accent="text-white border-p2w-line"
                />
                <div className="md:col-span-2 rounded-xl border border-p2w-line bg-p2w-elevated p-3 text-xs text-p2w-gray flex items-center" data-testid="settings-preview">
                    <div>
                        Ejemplo con un bote de <span className="text-white font-sora font-bold number-tabular">10.000 pts</span> ($
                        {Math.round(10000 * (form.point_to_peso_rate || 1)).toLocaleString("es-ES")} pesos):
                        <br />
                        <span className="text-p2w-green">jugadores {exampleSplit(playersPct).toLocaleString("es-ES")}</span> ·{" "}
                        <span className="text-p2w-purple">plataforma {exampleSplit(form.platform_pct).toLocaleString("es-ES")}</span> ·{" "}
                        <span className="text-p2w-magenta">creador {exampleSplit(form.creator_pct).toLocaleString("es-ES")}</span> ·{" "}
                        <span className="text-p2w-amber">liga {exampleSplit(form.league_pct).toLocaleString("es-ES")}</span>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between">
                <div className="text-[10px] text-p2w-gray">
                    Última config: L1={settings.l1_bonus} · L2={settings.l2_bonus} · L3={settings.l3_bonus} ·
                    Plat={Math.round((settings.platform_pct || 0) * 100)}% ·
                    Crea={Math.round((settings.creator_pct || 0) * 100)}% ·
                    Liga={Math.round((settings.league_pct || 0) * 100)}%
                </div>
                <Button
                    onClick={save}
                    disabled={saving || invalid}
                    data-testid="save-settings-btn"
                    className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold gap-2"
                >
                    <Save className="w-4 h-4" />
                    {saving ? "Guardando…" : "Guardar configuración"}
                </Button>
            </div>
        </div>
    );
}

function NumberField({ label, value, onChange, step = 1, testid, accent, suffix }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-widest text-p2w-gray">{label}</Label>
            <div className="relative mt-1.5">
                <Input
                    type="number"
                    step={step}
                    min={0}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testid}
                    className={`bg-p2w-elevated border text-xl font-sora font-bold number-tabular focus-visible:ring-p2w-green/40 ${accent || "text-white border-p2w-line"}`}
                />
                {suffix && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-p2w-gray text-sm pointer-events-none">
                        {suffix}
                    </span>
                )}
            </div>
        </div>
    );
}
